/* eslint-disable react/no-unused-prop-types */
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import isFunction from 'lodash/isFunction';
import { ParsedQs } from 'qs';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconFlag } from 'src/components/Icons/Flag';
import { UiButton } from 'src/components/UI/Button';
import { UiTableProps } from 'src/components/UI/table/UiTable';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/config';
import { BaseTableFilterFiltersType, CommonTableRequestType } from 'src/models/Table';
import { TableFlagRequestType } from 'src/models/TableFlags';
import { getUniqueId } from 'src/utils/getUniqueId';
import { omitNilObjectValues } from 'src/utils/objects';
import { SafeAnyType } from 'src/utils/safeAny';
import { ChildrenPropType, SortDirectionEnum } from 'src/utils/types';
import styles from './styles.module.scss';

export type UiTableInnerFiltersType = {
  PageSize: CommonTableRequestType['PageSize'];
  PageIndex: NonNullable<CommonTableRequestType['PageIndex']>;
  SortByProperty: NonNullable<CommonTableRequestType['SortByProperty']>;
  SortDirection: NonNullable<CommonTableRequestType['SortDirection']>;
  Filters: BaseTableFilterFiltersType;
  CheckboxFilters: NonNullable<CommonTableRequestType['CheckboxFilters']>;
} & TableFlagRequestType;

export type UiTableFilterType = {
  type:
    | 'integer'
    | 'boolean'
    | 'text'
    | 'date'
    | 'numeric'
    | 'intersect'
    | 'lookup'
    | 'anyContains'
    | 'phoneNumber'
    | 'lookupText';
  propertyName: string;
  selected?: number[]; // default selected values
  lookup?: { name: string; value: number | string | boolean }[]; // possible values
};

export type UiTableColumnItemType<T = SafeAnyType> = {
  title: string; // must be required always
  renderValue: ((item: T) => React.ReactNode | Element) | keyof T;

  className?: string;
  header?: string;
  sortPropertyName?: string;
  filter?: UiTableFilterType;
  defaultSortDirection?: SortDirectionEnum;
  visible?: boolean;
  flagged?: boolean;
  flagProperty?: string;
  popoverText?: string;
  useSortAndNotShow?: boolean; // useful when the column must have sorting but must not be seen

  marked?: (item: T) => boolean;
  markedContent?: ChildrenPropType;

  textEllipsis?: boolean;
  isCheckBox?: boolean;
  onCheckboxClick?: (data: {
    mode: 'selectItem' | 'selectAllPage';
    item: T | undefined;
    event: 'checked' | 'unchecked';
  }) => void;
  isChecked?: boolean;
  selectedKeyProps?: (string | number)[] | 'all';
  disabledItem?: boolean;
  disabledTH?: boolean;
};

// null for columns without data
export type UiTableColumnType<T = SafeAnyType> =
  | UiTableColumnItemType<T>
  | ((item: T | null) => UiTableColumnItemType<T>);

export type UiTableColumnOnCheckboxClickDataType<T = SafeAnyType> = Parameters<
  NonNullable<UiTableColumnItemType<T>['onCheckboxClick']>
>[0];

export type UiTableRefProps = {
  changeFilters: (obj: Partial<UiTableInnerFiltersType>, clearFilters?: boolean) => void;
  reloadData: () => void;
  getFilters: () => UiTableInnerFiltersType | undefined;
};

export const uiTableGetColumnDataWithItem = (columnRaw: UiTableColumnType, item: SafeAnyType) =>
  isFunction(columnRaw) ? columnRaw(item) : columnRaw;

/** get number type of string */
export const uiTableGetType = (type: UiTableFilterType['type']): number => {
  switch (type) {
    case 'text':
      return 1;
    case 'numeric':
      return 2;
    case 'date':
      return 3;
    case 'integer':
      return 4;
    case 'lookup':
      return 5;
    case 'boolean':
      return 6;
    case 'lookupText':
      return 7;
    case 'intersect':
      return 10;
    case 'phoneNumber':
      return 9;
    case 'anyContains':
      return 11;
    default:
      return 0;
  }
};

type UiTableLoadingPropsType = {
  dummyTable: UiTableProps['dummyTable'];
  colSpan: number;

  v2?: boolean;
};

export const UiTableLoadingPartial: React.FC<UiTableLoadingPropsType> = memo(
  ({ dummyTable, colSpan, v2 = false }: UiTableLoadingPropsType) => {
    const { t } = useTranslation();

    return (
      <tr className={dummyTable ? 'dummy-table-body' : undefined}>
        <td
          colSpan={colSpan}
          className={classNames({
            'text-center': dummyTable,
            [styles.td]: v2,
          })}
        >
          {dummyTable ? (
            <div className={v2 ? styles.bodyWrapper : undefined}>
              <div
                className={classNames('dummy-table-body-icon-background', styles.bodyIconBackground, {
                  PrimaryBrandColor: !v2,
                })}
              >
                <div className="dummy-table-body-icon" />
              </div>
              <div className={classNames(v2 ? 'dummy-table-body-text' : 'dummy-grid-body-text', styles.bodyText)}>
                {t('table.dummyLoadingText')}
              </div>
            </div>
          ) : (
            t('table.loading')
          )}
        </td>
      </tr>
    );
  }
);

type UiTableNotFoundPropsType = {
  dummyTable: UiTableProps['dummyTable'];
  colSpan: number;

  notFoundText?: UiTableProps['notFoundText'];
  v2?: boolean;
};

export const UiTableNotFoundPartial: React.FC<UiTableNotFoundPropsType> = memo(
  ({ dummyTable, colSpan, notFoundText, v2 }: UiTableNotFoundPropsType) => {
    const { t } = useTranslation();

    return (
      <tr
        className={classNames({
          'dummy-table-body': dummyTable,
          'nothing-found': dummyTable && v2,
        })}
      >
        <td colSpan={colSpan} className={classNames('text-center', v2 && styles.td)}>
          {dummyTable ? (
            <div className={v2 ? styles.bodyWrapper : undefined}>
              <div className={classNames('dummy-table-body-icon-background', styles.bodyIconBackground)}>
                <div className="dummy-table-body-icon">
                  {v2 && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <path
                        /* eslint-disable-next-line max-len */
                        d="M35.9263 11.9754C42.5402 18.5893 42.5402 29.3125 35.9263 35.9263C29.3125 42.5402 18.5893 42.5402 11.9754 35.9263C5.36159 29.3125 5.36159 18.5893 11.9754 11.9754C18.5893 5.36159 29.3125 5.36159 35.9263 11.9754ZM14.7328 33.169C19.8238 38.26 28.078 38.26 33.169 33.169C38.26 28.078 38.26 19.8238 33.169 14.7328C28.078 9.64181 19.8238 9.64181 14.7328 14.7328C9.64181 19.8238 9.64181 28.078 14.7328 33.169Z"
                        fill="#272756"
                      />
                      <rect
                        x="33.7182"
                        y="37.2585"
                        width="5.00639"
                        height="13.4572"
                        transform="rotate(-45 33.7182 37.2585)"
                        stroke="#272756"
                        strokeWidth="2.35116"
                      />
                      <rect
                        x="37.1934"
                        y="42.3973"
                        width="7.35891"
                        height="17.2944"
                        rx="1.17558"
                        transform="rotate(-45 37.1934 42.3973)"
                        fill="#272756"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <div className={classNames(v2 ? 'dummy-table-body-text' : 'dummy-grid-body-text', styles.bodyText)}>
                {notFoundText || t('table.notFound')}
              </div>
            </div>
          ) : (
            notFoundText || t('table.notFound')
          )}
        </td>
      </tr>
    );
  }
);

type UiTableActionsType = {
  manage: boolean;
  actions: (item: SafeAnyType) => React.ReactNode;
  item: SafeAnyType;
  open: boolean;
  onOpen: () => void;
};

export const UiTableActionsPartial: React.FC<UiTableActionsType> = memo(
  ({ manage, actions, item, open, onOpen }: UiTableActionsType) => {
    const { t } = useTranslation();

    return manage ? (
      <td className="manage">
        <div className="btn-group pull-right">
          <div className="buttons-container adjust-menu-direction">{open && actions(item)}</div>
        </div>
      </td>
    ) : (
      <td>
        <div
          className={classNames(`btn-group pull-right`, {
            open,
          })}
        >
          <UiButton
            tag="a"
            className="btn dropdown-toggle btn-xs btn-primary no-margin"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            {t('table.actionsBtn')}&nbsp;
            <span className="caret" />
          </UiButton>
          {open && <ul className="dropdown-menu">{actions(item)}</ul>}
        </div>
      </td>
    );
  }
);

type UiTableCheckboxProps = {
  onCheckboxClick: UiTableColumnItemType['onCheckboxClick'];
  isChecked: UiTableColumnItemType['isChecked'];

  className?: string;
  thead?: boolean;
  item?: SafeAnyType;
  disabled?: boolean;
};

export const UiTableCheckbox: React.FC<UiTableCheckboxProps> = ({
  onCheckboxClick,
  isChecked,
  thead,
  item,
  disabled,
}) => {
  const id = uniqueId();

  return (
    <div className="checkbox">
      <input
        className={classNames('filled-in', { disabled })}
        type="checkbox"
        checked={isChecked}
        disabled={disabled}
        id={id}
        onChange={(e) => {
          if (isFunction(onCheckboxClick) && !disabled) {
            onCheckboxClick({
              mode: thead ? 'selectAllPage' : 'selectItem',
              item,
              event: e.target.checked ? 'checked' : 'unchecked',
            });
          }
          e.stopPropagation();
        }}
      />
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label htmlFor={id} role="presentation" />
    </div>
  );
};

export const UiTableTDCheckbox: React.FC<UiTableCheckboxProps> = ({
  onCheckboxClick,
  isChecked,
  thead,
  item,
  className,
  disabled,
}) => {
  const id = uniqueId();

  return (
    <td
      role="button"
      className={className}
      onClick={(e) => {
        if (isFunction(onCheckboxClick) && !disabled) {
          onCheckboxClick({
            mode: thead ? 'selectAllPage' : 'selectItem',
            item,
            event: isChecked ? 'unchecked' : 'checked',
          });
        }
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="checkbox">
        <input
          className={classNames('filled-in', { disabled })}
          type="checkbox"
          checked={isChecked}
          disabled={disabled}
          id={id}
          readOnly
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor={id} role="presentation" />
      </div>
    </td>
  );
};

export const UiTableCreateTH =
  (item: SafeAnyType) =>
  // eslint-disable-next-line
  (columnRaw: UiTableColumnType, { itemKeyProp }: { itemKeyProp: string }): React.ReactNode => {
    const columnData = uiTableGetColumnDataWithItem(columnRaw, item);

    if (columnData.visible === false) {
      return null;
    }

    const {
      title,
      className,
      renderValue,
      flagged,
      flagProperty,
      isCheckBox,
      onCheckboxClick,
      disabledItem,
      marked,
      markedContent,
    } = columnData;

    const showFlag = !!(flagged && flagProperty && item[flagProperty]);
    const valueToRender = isFunction(renderValue) ? (renderValue(item) as React.ReactNode) : item[renderValue];

    const valueToRenderPrepared = columnData.textEllipsis ? (
      <div className="tableEllipsis">{valueToRender}</div>
    ) : (
      valueToRender
    );

    const isMarked = marked && marked(item);

    return isCheckBox ? (
      <UiTableTDCheckbox
        key={title || getUniqueId()}
        item={item}
        isChecked={
          columnData.selectedKeyProps === 'all' ? true : (columnData.selectedKeyProps || []).includes(item[itemKeyProp])
        }
        disabled={disabledItem}
        onCheckboxClick={onCheckboxClick}
        className={classNames(className, {
          flagged: showFlag,
        })}
      />
    ) : (
      <td
        key={title || getUniqueId()}
        className={classNames(className, {
          flagged: showFlag,
          marked: isMarked && markedContent,
        })}
      >
        {showFlag || (isMarked && markedContent) ? (
          <div className="wrapper">
            {valueToRenderPrepared}
            {markedContent || (
              <div className="box">
                <div className="flagbox">
                  <IconFlag />
                </div>
              </div>
            )}
          </div>
        ) : (
          valueToRenderPrepared
        )}
      </td>
    );
  };

type UiTableColumnSortType = {
  filters: UiTableInnerFiltersType;
  onChange: (obj: Partial<UiTableInnerFiltersType>) => void;

  name?: string;
  v2?: boolean;
};

export const UiTableColumnSort: React.FC<UiTableColumnSortType> = ({
  name,
  filters: { SortByProperty: propertyName, SortDirection: direction },
  onChange,
  v2,
}: UiTableColumnSortType) => {
  const onClick = () => {
    onChange({
      SortByProperty: name,
      SortDirection:
        name === propertyName && direction === SortDirectionEnum.ASC ? SortDirectionEnum.DESC : SortDirectionEnum.ASC,
      PageIndex: 0,
    });
  };

  if (name) {
    const isDesc = name === propertyName && direction === SortDirectionEnum.DESC;

    return v2 ? (
      <i
        role="presentation"
        className={classNames('grid-column-sort-icon', {
          muted: name !== propertyName,
          'arrow-downward': isDesc,
          'arrow-upward': !isDesc,
        })}
        onClick={onClick}
      >
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            /* eslint-disable-next-line max-len */
            d="M4.07141 0.798302L0.268745 6.35291C0.0930085 6.60944 -3.87765e-08 6.8679 -2.93858e-08 7.08274C-1.12306e-08 7.49808 0.333341 7.755 0.891312 7.755L9.10998 7.755C9.66731 7.755 10 7.4984 10 7.08403C10 6.86887 9.90691 6.61454 9.73069 6.35745L5.9281 0.800245C5.68316 0.442861 5.35354 0.244945 4.99955 0.244945C4.64581 0.244864 4.31628 0.440514 4.07141 0.798302Z"
            fill="#625EE9"
          />
        </svg>
      </i>
    ) : (
      <i
        role="presentation"
        className={classNames('material-icons', {
          muted: name !== propertyName,
        })}
        onClick={onClick}
      >
        {isDesc ? 'arrow_downward' : 'arrow_upward'}
      </i>
    );
  }
  return null;
};

export const UiTableGetInitialFilterValues = ({
  columns: columnsRaw,
  params,
  itemsPerPage = DEFAULT_TABLE_PAGE_SIZE,
  initialFilters,
}: {
  columns: UiTableColumnType[];
  params: ParsedQs;
  itemsPerPage?: number;
  initialFilters?: BaseTableFilterFiltersType;
}): UiTableInnerFiltersType => {
  const columns = columnsRaw.map((columnRaw) => uiTableGetColumnDataWithItem(columnRaw, null));

  const defaultSortProperty = columns.find(({ defaultSortDirection }) => defaultSortDirection);

  const filtersProperty: SafeAnyType[] = initialFilters?.length ? initialFilters : (params.Filters as string[]);

  const preparedFilters = omitNilObjectValues({
    PageSize: params.PageSize && +params.PageSize > 0 ? +params.PageSize : itemsPerPage,
    PageIndex: params.PageIndex && +params.PageIndex > 0 ? +params.PageIndex - 1 : 0,
    SortByProperty: params.SortByProperty || defaultSortProperty?.sortPropertyName,
    SortDirection: params.SortDirection || defaultSortProperty?.defaultSortDirection,
    Filters: filtersProperty
      ? filtersProperty.map((item) => {
          const type = item.FilterType ? +item.FilterType : uiTableGetType(item.Type);

          const obj: BaseTableFilterFiltersType[0] = {
            FilterType: type,
            PropertyName: item.PropertyName || item.Column,
          };

          if (item.Value) {
            obj.Value = type === 4 ? +item.Value : item.Value;
          } else if (item.ValueFrom || item.ValueTo) {
            let valueFrom = item.ValueFrom;
            let valueTo = item.ValueTo;

            if (type === 2 && valueFrom) {
              valueFrom = +valueFrom;
            }
            if (type === 2 && valueTo) {
              valueTo = +valueTo;
            }

            obj.ValueFrom = valueFrom;
            obj.ValueTo = valueTo;
          }

          return obj;
        })
      : [],
    CheckboxFilters: [],
    OnlyFlagged: params.OnlyFlagged ? params.OnlyFlagged === 'true' : undefined,
  }) as UiTableInnerFiltersType;

  // add default filter values only if filters are not empty
  // for example if to search by id, no selected statuses should be checked
  if (!params.Filters) {
    columns
      .filter(({ filter, useSortAndNotShow }) => !((filter?.selected?.length || 0) === 0 || useSortAndNotShow))
      .forEach((item) => {
        const { propertyName, selected, type } = item.filter as Required<UiTableFilterType>;

        if (!~preparedFilters.Filters.findIndex(({ PropertyName }) => PropertyName === propertyName)) {
          preparedFilters.Filters.push({
            FilterType: uiTableGetType(type),
            PropertyName: propertyName,
            Value: selected.join(),
          });
        }
      });
  }

  return preparedFilters;
};
